// --- src/components/Menu.js ---

import { React, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppContext } from '../AppContext';
import { Plus, Menu as MenuIcon, X, Folder, Undo, Redo, Search, UserCircle, Settings, AlertTriangle, MoreHorizontal, Heart, HandHeart, Rss } from 'lucide-react';
import DonationPopup from './DonationPopup';

const Menu = () => {
  const location = useLocation();
  const { menuConfig, updateScrollPosition } = useAppContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDonationPopupOpen, setIsDonationPopupOpen] = useState(false);

  const artifactCount = menuConfig.length;

  const handleMenuClick = (artifactId) => {
    const currentArtifactId = location.pathname.split('/artifact/')[1];
    if (currentArtifactId && currentArtifactId !== artifactId) {
      updateScrollPosition(currentArtifactId);
    }
    setIsMenuOpen(false);
  };

  const openDonationPopup = () => {
    setIsDonationPopupOpen(true);
    setIsMenuOpen(false);
  };

  const closeDonationPopup = () => {
    setIsDonationPopupOpen(false);
  };

  const donateButtonTextDesktop = "Donate for Project R&D";
  const donateButtonTextMobile = "Donate for R&D";
  const blogButtonTextDesktop = "Read Blog Post";
  const blogButtonTextMobile = "Blog";

  return (
    <>
      {/* Fixed Panel for Menu Button (Mobile Only) */}
      <div className={`${isMenuOpen ? 'hidden' : 'block'} fixed top-0 left-0 right-0 bg-white shadow-md z-50 p-2 flex items-center justify-between lg:hidden h-[58px]`}>
        {/* Menu Button */}
        <button className={`text-gray-700 p-2 rounded hover:bg-gray-100 flex-shrink-0`} onClick={() => setIsMenuOpen(true)}>
          <MenuIcon size={24} />
        </button>

        {/* Simplified Command Input (Mobile Menu Closed) */}
        <div className="flex-grow mx-1 mr-3 min-w-0">
           <div className="bg-gray-100 border border-gray-200 rounded-md flex items-center px-2 py-2 space-x-1.5 w-full">
              <Search size={14} className="text-gray-500 flex-shrink-0" />
              <span className="text-gray-600 text-xs truncate flex-grow min-w-0">Command</span>
           </div>
        </div>


        {/* Mobile Action Buttons Container */}
        <div className="flex items-center space-x-2 flex-shrink-0">
            <Link
              to="https://blog.kryzhnii.me/blog/crosscontext-ux-design"
              className="p-2 text-blue-600 border border-blue-600 hover:bg-blue-100 rounded flex items-center text-xs font-medium"
              title={blogButtonTextDesktop}
              onClick={() => setIsMenuOpen(false)}
            >
              <Rss size={16} className="mr-1"/> {blogButtonTextMobile}
            </Link>
            <button
              onClick={openDonationPopup}
              className="p-2 text-red-600 border border-red-600 hover:bg-red-100 rounded flex items-center text-xs font-medium"
              title={donateButtonTextDesktop}
            >
              <HandHeart size={16} className="mr-1"/> {donateButtonTextMobile}
            </button>
        </div>
      </div>

      {/* Menu Navigation */}
      <nav
        className={`fixed top-0 left-0 h-screen bg-gray-800 text-white transition-transform duration-300 ease-in-out w-full lg:w-60 ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 z-40 flex flex-col`}
      >
        {/* Top Control Panel */}
        <div className="relative p-3 border-b border-gray-700 flex justify-between items-center after:content-[''] after:absolute after:inset-0 after:bg-black/25 after:z-10 after:pointer-events-none h-[53px]">
          {/* New Button + Gap */}
          <button className="px-3 py-1 rounded-md bg-gray-700 text-gray-400 cursor-not-allowed flex items-center justify-center text-sm hover:bg-gray-600 flex-shrink-0 mr-2" disabled title="Add New Artifact (not available in static prototype)" >
            <Plus size={16} className="mr-1" /> New
          </button>

          {/* Undo/Redo Group - UPDATED: Hidden on large screens */}
          <div className="flex space-x-2 flex-shrink-0 lg:hidden"> {/* Added lg:hidden */}
             <button className="p-1.5 text-gray-400 bg-gray-700 hover:bg-gray-600 rounded" disabled title="Undo (not available in static prototype)"> <Undo size={16} /> </button>
             <button className="p-1.5 text-gray-400 bg-gray-700 hover:bg-gray-600 rounded" disabled title="Redo (not available in static prototype)"> <Redo size={16} /> </button>
          </div>

          {/* Simplified Command Input (Mobile Menu Open) */}
          <div className="flex-grow mx-2 lg:hidden">
             <div className="bg-gray-700 rounded flex items-center px-2 space-x-1.5 w-full py-1.5">
                <Search size={14} className="text-gray-400 flex-shrink-0" />
                <span className="text-gray-400 text-xs truncate">Command Menu...</span>
             </div>
          </div>

          {/* Placeholder div to maintain layout balance on desktop when Undo/Redo is hidden */}
          <div className="hidden lg:block flex-grow mx-2"></div> {/* This fills the space on desktop */}


          {/* Close Button (Mobile) */}
          <button className="p-1.5 text-gray-400 bg-gray-700 hover:bg-gray-600 rounded lg:hidden flex-shrink-0" onClick={() => setIsMenuOpen(false)} title="Close Menu">
             <X size={16} />
          </button>
        </div>


        {/* Internal Search Bar */}
        <div className="p-3 border-b border-gray-700">
          <div className="relative">
            <input type="search" placeholder="Search artifacts... (not active)" className="w-full p-1.5 pl-7 border-0 rounded-md bg-gray-700 text-sm text-gray-300 placeholder-gray-500 focus:ring-1 focus:ring-blue-500" disabled title="Search functionality not available in static prototype" />
            <Search size={14} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500" />
          </div>
        </div>

        {/* Main Artifact List Area */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider">CrossContext Demo</div>
          <div className="flex items-center justify-between mb-1 text-gray-400">
            <div className="flex items-center text-sm font-medium pl-1">
              <Folder size={16} className="mr-1.5 flex-shrink-0" />
              <span className="truncate">Artifacts</span>
              <span className="ml-1.5 text-sm font-normal">({artifactCount})</span>
            </div>
          </div>
          <ul className="pl-4 border-l border-gray-600 ml-[5px]">
            {menuConfig.map((item) => (
              <li key={item.id} className="mb-1 relative before:content-[''] before:absolute before:w-3 before:h-px before:bg-gray-600 before:left-[-1rem] before:top-1/2 before:-translate-y-1/2" >
                <Link
                  to={item.path}
                  className={`block pl-3 pr-3 py-1 rounded-md hover:bg-gray-700 transition-colors duration-200 text-sm truncate ${
                    location.pathname.startsWith(item.path) ? 'bg-blue-600 text-white font-medium' : 'text-gray-300'
                  }`}
                  onClick={() => handleMenuClick(item.id)}
                  title={item.label}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-4 p-2 text-xs text-gray-400 bg-gray-700/50 rounded italic flex items-start">
             <AlertTriangle size={16} className="text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
             <span>
               Click artifact links above to switch contexts and explore how CrossContext organizes information.
             </span>
          </div>
           {/* Desktop Action Buttons */}
           <div className="mt-4 space-y-2">
             <Link
               to="https://blog.kryzhnii.me/blog/crosscontext-ux-design"
               className="w-full px-3 py-1.5 rounded-md bg-gray-700 border border-gray-600 text-blue-400 hover:bg-gray-600 hover:text-blue-300 flex items-center justify-center text-sm transition-colors duration-200"
               title={blogButtonTextDesktop}
               onClick={() => setIsMenuOpen(false)}
             >
               <Rss size={16} className="mr-1.5" /> {blogButtonTextDesktop}
             </Link>
             <button
               onClick={openDonationPopup}
               className="w-full px-3 py-1.5 rounded-md bg-gray-700 border border-gray-600 text-red-400 hover:bg-gray-600 hover:text-red-300 flex items-center justify-center text-sm transition-colors duration-200"
               title={donateButtonTextDesktop}
             >
               <HandHeart size={16} className="mr-1.5" /> {donateButtonTextDesktop}
             </button>
           </div>
        </div>

        {/* User/Settings Area */}
        <div className="relative mt-auto p-3 border-t border-gray-700 flex justify-between items-center after:content-[''] after:absolute after:inset-0 after:bg-black/25 after:z-10 after:pointer-events-none">
          <div className="flex items-center space-x-2">
            <UserCircle size={20} className="text-gray-500" />
            <span className="text-sm text-gray-400">User Name</span>
          </div>
          <button className="p-1 text-gray-500 hover:text-gray-300" disabled title="Settings (not active)">
            <Settings size={18} />
          </button>
        </div>
      </nav>

      {/* Donation Popup Modal */}
      {isDonationPopupOpen && <DonationPopup onClose={closeDonationPopup} />}
    </>
  );
};

export default Menu;